import { plainToInstance, Type } from 'class-transformer'

import { Entity } from '@core/entities/entity'
import {
  BriefingSessionData,
  BriefingSessionFormValue,
  BriefingSessionStatus,
} from '@core/entities/briefing'
import { IBriefingSession } from './IBriefingSession'

export class BriefingSession extends Entity<IBriefingSession> {
  id: number
  organisationId: number
  referenceObjectId: number
  referenceObjectType: string

  @Type(() => BriefingSessionData)
  data: BriefingSessionData

  createdOn: string
  modifiedOn: string
  status: BriefingSessionStatus
  version: number

  public static new(payload: unknown): BriefingSession {
    const entity = plainToInstance(BriefingSession, payload)
    return entity
  }

  public getUnsignedParticipants() {
    return this.data.participants.filter(
      (participant) => !participant.signatureKey,
    )
  }

  public getSignedParticipants() {
    return this.data.participants.filter(
      (participant) => !!participant.signatureKey,
    )
  }

  // Seeds the participants stage of the request/draft forms with the
  // session's not-yet-briefed participants.
  public getParticipantsFormValues(): BriefingSessionFormValue {
    return {
      briefingSessionId: this.id,
      participants: this.getUnsignedParticipants().map((participant) => ({
        sessionParticipantId: participant.sessionParticipantId,
        userId: participant.userId,
        firstName: participant.firstName,
        lastName: participant.lastName,
        company: participant.company,
        roleId: participant.briefOnRoleId,
      })),
    }
  }

  // Builds the `briefingSession` request payload from a form value as a full
  // rewrite of the participant list. An emptied list is sent as `undefined`
  // (a no-op) rather than `[]`: the backend rejects an empty list
  // (`participants` is `@NotEmpty`) and offers no clear-to-zero path, so the
  // last participant can't be removed — emptying just leaves the existing
  // session untouched.
  public static toRequestPayload(formValue?: {
    briefingSessionId?: number
    participants?: BriefingSessionFormValue['participants']
  }) {
    if (!formValue?.participants?.length) {
      return undefined
    }

    return {
      briefingSessionId: formValue.briefingSessionId,
      participants: formValue.participants.map((participant) => ({
        participantId: participant.participantId,
        userId: participant.userId,
        sessionParticipantId: participant.sessionParticipantId,
        firstName: participant.firstName,
        lastName: participant.lastName,
        company: participant.company,
        briefOnRoleId: participant.roleId,
      })),
    }
  }
}
